import { supabase, Profile, TowJob } from '../lib/supabase'

/**
 * Automated Tower Service
 * Handles tower selection, pricing, and automated payment processing
 */

export interface TowerWithRate extends Profile {
  distance_km: number
  estimated_cost: number
  eta_minutes: number
}

/**
 * Get nearby towers with their rates for a given location
 * @param lat - Latitude
 * @param lng - Longitude
 * @param serviceType - Type of service needed
 * @param distanceKm - Search radius in km
 * @returns Array of towers with rates and estimated costs
 */
export async function getNearbyTowersWithRates(
  lat: number,
  lng: number,
  serviceType: 'towing' | 'battery' | 'flat_tyre' | 'fuel',
  distanceKm: number = 20
): Promise<TowerWithRate[]> {
  try {
    // Get all towers (changed from 'operator' to 'tower' to match actual role)
    const { data: towers, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('role', 'tower')
    
    if (error) {
      console.error('Error fetching towers:', error)
      return []
    }

    console.log('Fetched towers:', towers?.length, towers)
    console.log('Search location:', lat, lng)

    // Calculate distance and estimated cost for each tower
    const towersWithRates = (towers || []).map((tower, index) => {
      // If tower has no coordinates, use a default location near the search point
      const towerLat = tower.latitude || lat + (Math.random() - 0.5) * 0.1
      const towerLng = tower.longitude || lng + (Math.random() - 0.5) * 0.1
      
      const distance = calculateDistance(lat, lng, towerLat, towerLng)
      
      console.log(`Tower ${index}: ${tower.full_name}, coords: ${towerLat}, ${towerLng}, distance: ${distance.toFixed(2)}km`)
      
      // If outside search radius, still include for testing but note the distance
      // if (distance > distanceKm) return null

      // Calculate estimated cost based on service type
      let estimatedCost = 0
      switch (serviceType) {
        case 'towing':
          estimatedCost = (tower.base_towing_rate || 1500) + (distance * (tower.per_km_rate || 100))
          break
        case 'battery':
          estimatedCost = tower.battery_service_rate || 2000
          break
        case 'flat_tyre':
          estimatedCost = tower.flat_tyre_rate || 1500
          break
        case 'fuel':
          // For fuel delivery, distance is from tower to customer (breakdown location)
          // Tower will then go to nearest petrol station from customer location
          estimatedCost = (tower.fuel_delivery_base_rate || 500) + (distance * (tower.fuel_delivery_per_km_rate || 50))
          break
      }

      // Calculate ETA (assuming 40km/h average speed in Nairobi traffic)
      const etaMinutes = Math.round((distance / 40) * 60)

      return {
        ...tower,
        distance_km: distance,
        estimated_cost: estimatedCost,
        eta_minutes: etaMinutes
      }
    }).filter((tower): tower is TowerWithRate => tower !== null)

    console.log('Towers with rates:', towersWithRates.length, towersWithRates)

    // Sort by distance and cost
    return towersWithRates.sort((a, b) => {
      // First sort by distance, then by cost
      if (Math.abs(a.distance_km - b.distance_km) < 2) {
        return a.estimated_cost - b.estimated_cost
      }
      return a.distance_km - b.distance_km
    })
  } catch (error) {
    console.error('Error getting nearby towers with rates:', error)
    return []
  }
}

/**
 * Calculate distance between two points using Haversine formula
 */
function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371 // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLng = (lng2 - lng1) * Math.PI / 180
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLng/2) * Math.sin(dLng/2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
  return R * c
}

/**
 * Auto-generate payment reference number
 * @returns Generated reference number
 */
export function generatePaymentReference(): string {
  const timestamp = Date.now().toString().slice(-8)
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0')
  return `PAY-${timestamp}-${random}`
}

/**
 * Auto-generate document number
 * @returns Generated document number
 */
export function generateDocumentNumber(): string {
  const date = new Date()
  const dateStr = date.toISOString().slice(0, 10).replace(/-/g, '')
  const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0')
  return `DOC-${dateStr}-${random}`
}

/**
 * Customer selects a tower for their job
 * @param jobId - The job ID
 * @param towerId - The selected tower ID
 * @param estimatedCost - The estimated cost from tower's rates
 * @returns Selection result
 */
export async function customerSelectTower(
  jobId: string,
  towerId: string,
  estimatedCost: number
): Promise<{ success: boolean; message: string; error?: string }> {
  try {
    const { error } = await supabase
      .from('tow_jobs')
      .update({
        selected_tower_id: towerId,
        service_price: estimatedCost,
        total_amount: estimatedCost,
        updated_at: new Date().toISOString()
      })
      .eq('id', jobId)

    if (error) {
      return {
        success: false,
        message: 'Failed to select tower',
        error: error.message
      }
    }

    return {
      success: true,
      message: 'Tower selected successfully'
    }
  } catch (error) {
    return {
      success: false,
      message: 'Tower selection failed',
      error: error instanceof Error ? error.message : 'Unknown error'
    }
  }
}

/**
 * Tower accepts a job
 * @param jobId - The job ID
 * @param towerId - The tower ID
 * @returns Accept result
 */
export async function towerAcceptJob(
  jobId: string,
  towerId: string
): Promise<{ success: boolean; message: string; error?: string }> {
  try {
    const { error } = await supabase
      .from('tow_jobs')
      .update({
        operator_id: towerId,
        status: 'dispatched',
        tower_accepted_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', jobId)
      .eq('selected_tower_id', towerId)

    if (error) {
      return {
        success: false,
        message: 'Failed to accept job',
        error: error.message
      }
    }

    return {
      success: true,
      message: 'Job accepted successfully'
    }
  } catch (error) {
    return {
      success: false,
      message: 'Job acceptance failed',
      error: error instanceof Error ? error.message : 'Unknown error'
    }
  }
}

/**
 * Tower declines a job
 * @param jobId - The job ID
 * @param towerId - The tower ID
 * @returns Decline result
 */
export async function towerDeclineJob(
  jobId: string,
  towerId: string
): Promise<{ success: boolean; message: string; error?: string }> {
  try {
    const { error } = await supabase
      .from('tow_jobs')
      .update({
        tower_declined_at: new Date().toISOString(),
        selected_tower_id: null,
        updated_at: new Date().toISOString()
      })
      .eq('id', jobId)
      .eq('selected_tower_id', towerId)

    if (error) {
      return {
        success: false,
        message: 'Failed to decline job',
        error: error.message
      }
    }

    return {
      success: true,
      message: 'Job declined successfully'
    }
  } catch (error) {
    return {
      success: false,
      message: 'Job decline failed',
      error: error instanceof Error ? error.message : 'Unknown error'
    }
  }
}